import type { BalanceWeaponId, PlayableWeaponId } from '../reference/weapon-progression-v0.js';
import type { SkillApplicationV2 } from '../skill-engine/types.js';
import {
  BALANCE_BOW_REFERENCE_RANGE_M,
  BALANCE_SPECIAL_GEOMETRY_V0,
  BALANCE_WEAPON_GEOMETRY_V0,
} from '../sim/basic-attack-geometry.js';
import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import { resolveWeaponContactApplication } from '../sim/weapon-contact.js';
import { blastCatalystRadiusM } from '../sim/catalyst-basic.js';
import type { FootprintPreview } from './craft-footprint.js';

/** A plain footprint: one sector, corridor or circle. */
export type ShapePreview =
  | { readonly kind: 'sector'; readonly reachM: number; readonly angleDeg: number; readonly label: string }
  | { readonly kind: 'corridor'; readonly lengthM: number; readonly widthM: number; readonly label: string }
  | { readonly kind: 'circle'; readonly center: 'caster' | 'aim' | 'nearest-enemy'; readonly radiusM: number; readonly acquisitionRangeM?: number; readonly label: string; readonly persistent?: boolean };

export type RangePreview =
  | ShapePreview
  | FootprintPreview
  | { readonly kind: 'ranged-basic'; readonly acquisitionRangeM: number; readonly travelRangeM: number; readonly projectileWidthM: number; readonly explosionRadiusM?: number; readonly labels: { readonly acquisition: string; readonly travel: string } };

function metres(value: number): string { return String(Number(value.toFixed(1))); }
function valid(value: number): boolean { return Number.isFinite(value) && value > 0; }

export function basicAttackRangePreview(weaponId: PlayableWeaponId, rawMag = 0): RangePreview {
  if (weaponId === 'BOW_REFERENCE' || weaponId === 'ARCANE_CATALYST' || weaponId === 'BLAST_CATALYST' || weaponId === 'RAPID_CATALYST') {
    return { kind: 'ranged-basic', acquisitionRangeM: BALANCE_BOW_REFERENCE_RANGE_M, travelRangeM: 18, projectileWidthM: .5, ...(weaponId === 'BLAST_CATALYST' ? { explosionRadiusM: blastCatalystRadiusM(rawMag) } : {}), labels: { acquisition: '조준 12m', travel: '비행 18m' } };
  }
  if (weaponId === 'ASSASSIN_BLADE' || weaponId === 'GREATSWORD') {
    const profile = BALANCE_SPECIAL_GEOMETRY_V0[weaponId];
    return { kind: 'sector', reachM: profile.reachM, angleDeg: profile.sectorDeg, label: `기본공격 몸 밖 ${metres(profile.reachM - PLAYER_RADIUS_M)}m` };
  }
  const profile = BALANCE_WEAPON_GEOMETRY_V0[weaponId];
  const angleDeg = profile.id === 'DAGGER_PAIR'
    ? profile.leftSweepDeg + profile.rightSweepDeg - profile.overlapDeg
    : profile.id === 'SPEAR' ? profile.acquisitionDeg : profile.sweepDeg;
  return { kind: 'sector', reachM: profile.effectiveReachM, angleDeg, label: `기본공격 몸 밖 ${metres(profile.effectiveReachM - PLAYER_RADIUS_M)}m` };
}

/**
 * Everything one release of this skill's application covers. Usually one shape;
 * a thrust that has a close sweep (the spear) covers its corridor when the
 * target is far and its sector when the target is within the close-sweep
 * distance, so both are shown.
 */
export function skillRangePreviews(application: SkillApplicationV2, name: string): ShapePreview[] {
  const main = skillRangePreview(application, name);
  if (main === undefined) return [];
  const resolved = application.geometry.owner === 'WEAPON_CONTACT' ? resolveWeaponContactApplication(application) : undefined;
  const close = resolved?.ok === true ? resolved.value.closeSweep : undefined;
  return close !== undefined && valid(close.reachM) && valid(close.angleDeg)
    ? [main, { kind: 'sector', reachM: close.reachM, angleDeg: close.angleDeg, label: `${name} 근접 ${metres(close.reachM)}m` }]
    : [main];
}

export function skillRangePreview(application: SkillApplicationV2, name: string): ShapePreview | undefined {
  if (application.motion.kind === 'PROJECTILE') {
    const lengthM = application.motion.maxTravelM; const widthM = application.motion.collisionRadiusM * 2;
    return valid(lengthM) && valid(widthM) ? { kind: 'corridor', lengthM, widthM, label: `${name} ${metres(lengthM)}m` } : undefined;
  }
  if (application.geometry.owner === 'WEAPON_CONTACT') {
    const resolved = resolveWeaponContactApplication(application);
    // A thrust is its corridor; a close sweep it may turn into is added by `skillRangePreviews`.
    if (resolved.ok && resolved.value.thrust === true) {
      return valid(resolved.value.reachM) && valid(resolved.value.widthM)
        ? { kind: 'corridor', lengthM: resolved.value.reachM, widthM: resolved.value.widthM, label: `${name} ${metres(resolved.value.reachM)}m` }
        : undefined;
    }
    return resolved.ok && valid(resolved.value.reachM) && valid(resolved.value.angleDeg)
      ? { kind: 'sector', reachM: resolved.value.reachM, angleDeg: resolved.value.angleDeg, label: `${name} ${metres(resolved.value.reachM)}m` }
      : undefined;
  }
  const shape = application.geometry.shape;
  if (shape.kind === 'SECTOR') return valid(shape.reachM) && valid(shape.angleDeg) ? { kind: 'sector', reachM: shape.reachM, angleDeg: shape.angleDeg, label: `${name} ${metres(shape.reachM)}m` } : undefined;
  if (shape.kind === 'CAPSULE') return valid(shape.lengthM) && valid(shape.widthM) ? { kind: 'corridor', lengthM: shape.lengthM, widthM: shape.widthM, label: `${name} ${metres(shape.lengthM)}m` } : undefined;
  if (shape.kind === 'CIRCLE') {
    if (!valid(shape.radiusM)) return undefined;
    const acquisitionRangeM = application.acquisitionRangeM;
    return {
      kind: 'circle', center: application.placement.kind === 'CASTER' ? 'caster' : 'aim', radiusM: shape.radiusM,
      ...(acquisitionRangeM !== undefined && valid(acquisitionRangeM) ? { acquisitionRangeM } : {}),
      label: `${name} 반경 ${metres(shape.radiusM)}m`,
    };
  }
  const rangeM = application.acquisitionRangeM;
  return rangeM !== undefined && valid(rangeM)
    ? { kind: 'circle', center: 'caster', radiusM: rangeM, label: `${name} ${metres(rangeM)}m` }
    : undefined;
}
